import { useEffect, useMemo, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { formatLocalDateValue, parseLocalDateValue } from '@/libs/date/localDate';

interface InterviewDayFilterProps {
  value: string;
  onChange: (value: string) => void;
}

const monthLabelFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  year: 'numeric',
});

const weekdayLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export function InterviewDayFilter({ value, onChange }: InterviewDayFilterProps) {
  const selectedDate = useMemo(() => (value ? parseLocalDateValue(value) : null), [value]);
  const [open, setOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(() => selectedDate ?? new Date());

  useEffect(() => {
    if (selectedDate) {
      setVisibleMonth(selectedDate);
    }
  }, [selectedDate]);

  const calendarDays = useMemo(() => {
    const monthStart = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1);
    const gridStart = new Date(monthStart);
    gridStart.setDate(monthStart.getDate() - monthStart.getDay());

    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + index);
      return date;
    });
  }, [visibleMonth]);

  const changeMonth = (offset: number) => {
    setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() + offset, 1));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="h-10 w-36 shrink-0 justify-start gap-2 border-border-subtle bg-card px-3 text-sm font-normal text-text-main shadow-xs hover:bg-primary-light/40"
          aria-label="Filter by interview day"
        >
          <CalendarDays size={15} className="text-text-subtle" />
          <span className={value ? 'text-text-main' : 'text-text-subtle'}>
            {value || 'Interview day'}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-64 rounded-lg border border-primary-light bg-card p-3 shadow-lg"
      >
        <div className="mb-3 flex items-center justify-between">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="text-text-main hover:bg-primary-light"
            onClick={() => changeMonth(-1)}
            aria-label="Previous month"
          >
            <ChevronLeft size={16} />
          </Button>
          <div className="text-sm font-semibold text-text-main">
            {monthLabelFormatter.format(visibleMonth)}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="text-text-main hover:bg-primary-light"
            onClick={() => changeMonth(1)}
            aria-label="Next month"
          >
            <ChevronRight size={16} />
          </Button>
        </div>

        <div className="grid grid-cols-7 text-center text-xs font-medium">
          {weekdayLabels.map((label) => (
            <div
              key={label}
              className={label === 'Su' || label === 'Sa' ? 'text-destructive' : 'text-text-subtle'}
            >
              {label}
            </div>
          ))}
        </div>

        <div className="mt-2 grid grid-cols-7 gap-y-1 text-center text-sm">
          {calendarDays.map((date) => {
            const dateValue = formatLocalDateValue(date);
            const isCurrentMonth = date.getMonth() === visibleMonth.getMonth();
            const isSelected = value === dateValue;

            return (
              <button
                key={dateValue}
                type="button"
                className={`mx-auto flex size-8 items-center justify-center rounded-md border text-sm transition-colors ${
                  isSelected
                    ? 'border-primary bg-primary-light font-semibold text-primary ring-4 ring-primary-light'
                    : 'border-transparent text-text-main hover:bg-primary-light/70'
                } ${isCurrentMonth ? '' : 'text-text-subtle/70'}`}
                onClick={() => {
                  onChange(dateValue);
                  setOpen(false);
                }}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>

        {value ? (
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              className="text-xs font-medium text-primary hover:underline"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
            >
              Clear
            </button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
